import { useCallback, useState } from 'react';
import { Grid, Autocomplete, TextField as MuiTextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { FormModal } from './FormModal';
import { TextInput, SelectInput } from '../inputs';
import { useDispatch, addResource, deleteResource } from '../../../state';
import { useRefresh } from '../../../hooks';
import { orderfulLabelTypeCodes } from '../../../lib/orderfulLabelTypeCodes';

const retailerTypeOptions = [{ id: 'Orderful', name: 'Orderful' }];

const initialState = {
  name: '',
  externalId: '',
  type: 'Orderful',
  orderfulLabelTypeCode: '',
};

export interface RetailerRecord {
  id?: number | string;
  name?: string;
  externalId?: string;
  type?: string;
  orderfulLabelTypeCode?: string;
}

interface RetailerFormProps {
  open: boolean;
  handleClose: () => void;
  retailerID?: number | string | null;
  initialData?: RetailerRecord;
}

export function RetailerForm({
  open,
  handleClose,
  retailerID,
  initialData,
}: RetailerFormProps) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const refresh = useRefresh();

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (values: typeof initialState) => {
      setSubmitting(true);
      const { error } = await dispatch(
        addResource({
          baseUrl: '/system/retailer',
          payload: { ...values, id: retailerID || undefined },
          message: 'Changes saved',
        }),
      );
      setSubmitting(false);
      if (!error) {
        refresh();
        handleClose();
      }
    },
    [dispatch, refresh, handleClose, retailerID],
  );

  const handleDelete = useCallback(async () => {
    setSubmitting(true);
    const response = await dispatch(
      deleteResource({
        baseUrl: '/system/retailer',
        id: retailerID!,
      }),
    );
    setSubmitting(false);
    const { error } = response;
    if (!error) {
      refresh();
      handleClose();
    }
  }, [dispatch, handleClose, refresh, retailerID]);

  const formik = useFormik({
    initialValues: { ...initialState, ...initialData },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={<span className={classes.title}>Add retailer</span>}
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      maxWidth='lg'
      paperProps={{ style: { width: 720 } }}
      submitting={submitting}
      isEdit={!!retailerID}
      handleDelete={handleDelete}
    >
      <div className={classes.layout}>
        <Grid container spacing={5} rowSpacing={2}>
          <Grid
            style={{ paddingBottom: 0 }}
            size={{
              sm: 6,
            }}
          >
            <TextInput
              name='name'
              formikProps={formik}
              label='Name'
              className={classes.input}
              required
            />
          </Grid>
          <Grid
            style={{ paddingBottom: 0 }}
            size={{
              sm: 6,
            }}
          >
            <TextInput
              name='externalId'
              formikProps={formik}
              label='External ID'
              className={classes.input}
            />
          </Grid>
          <Grid
            style={{ paddingBottom: 0 }}
            size={{
              sm: 6,
            }}
          >
            <SelectInput
              name='type'
              formikProps={formik}
              label='Type'
              options={retailerTypeOptions}
              className={classes.input}
            />
          </Grid>
          <Grid
            style={{ paddingBottom: 0 }}
            size={{
              sm: 6,
            }}
          >
            <Autocomplete
              freeSolo
              options={orderfulLabelTypeCodes}
              value={formik.values.orderfulLabelTypeCode}
              onChange={(_, value) =>
                formik.setFieldValue('orderfulLabelTypeCode', value ?? '')
              }
              onInputChange={(_, value) =>
                formik.setFieldValue('orderfulLabelTypeCode', value ?? '')
              }
              renderInput={({ inputProps: acInputProps, ...restParams }) => (
                <MuiTextField
                  {...restParams}
                  name='orderfulLabelTypeCode'
                  variant='standard'
                  label='Orderful Label Type Code'
                  className={classes.input}
                  slotProps={{
                    htmlInput: { ...acInputProps, 'data-lpignore': true },
                    inputLabel: { className: classes.label },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </div>
    </FormModal>
  );
}

const schema = object().shape({
  name: string().nullable().required('Required'),
});

const useStyles = makeStyles()((theme) => ({
  layout: {
    padding: '0 16px 16px 16px',
  },
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
    padding: '24px 16px',
  },
  input: {
    width: '100%',
  },
  label: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: 16,
  },
}));
